import cx from 'classnames';
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BaseComponentProps } from '../../../lib/types';
import Image, { type ImageCombinedProps } from '../../atoms/Image';
import { Spinner } from '../../atoms/Spinner';
import Text from '../../atoms/Text';
import { Carousel } from '../../molecules/Carousel';
import { ExclusiveBadge } from '../../molecules/ExclusiveBadge';
import { IconText } from '../../molecules/IconText';
import { ListingCard, type ListingCardProps } from '../../molecules/ListingCard';
import { RewardsSwitcher } from '../../molecules/RewardsSwitcher';
import { SignInDialog } from '../../molecules/SignInDialog';
import { SoldOutCard } from '../../molecules/SoldOutCard';
import { ExpandableContent } from '../../organisms/ExpandableContent';
import { GuestListPreCheckoutModal } from '../../organisms/GuestListPreCheckoutModal';
import styles from './GuestListEventPage.module.scss';
import { usePresenter } from './GuestListEventPage.presenter';
import type { GuestListEventPagePresenterProps, GuestListEventPageProps } from './GuestListEventPage.types';

export const GuestListEventPage: React.FC<GuestListEventPageProps> = (props) => {
  const presenterProps: GuestListEventPagePresenterProps = usePresenter(props);

  const {
    eventMetadata,
    areListingsLoading,
    carouselImageProps,
    listingCardProps,
    shouldOpenPreCheckout,
    isListingDetailLoading,
    listingDetailQueryParams,
    listingDetail,
    className,
    eventCardExclusiveBadgeProps,
    listingsMetadata,
  } = presenterProps;

  const {
    eventName,
    eventDescription,
    eventDateTimeAndVenueDetails,
    isPayWithRewardsOnly,
    isSoldOut,
  } = eventMetadata;

  const { t } = useTranslation();

  const topContent: ReactNode = (
    <Carousel<BaseComponentProps & ImageCombinedProps>
      isLoading={false}
      isError={false}
      isEmptyStateSupported={false}
      emptyStateTitleKey=''
      emptyStateDescriptionKey=''
      component={Image}
      componentProps={carouselImageProps}
      theme='dark'
      areBoxShadowsEnabled={false}
      classes={{
        carouselItemsContainer: styles.carouselImagesContainer,
        carouselBackwardButton: styles.carouselBackwardButton,
        carouselForwardButton: styles.carouselForwardButton,
      }}
    />
  );

  const cardholderExclusiveBadge: ReactNode = eventCardExclusiveBadgeProps && (
    <ExclusiveBadge
      {...eventCardExclusiveBadgeProps}
    />
  );

  const eventNameView: ReactNode = (
    <Text
      className={styles.eventNameView}
      type='Subheading'
      size='Large'
      style='SemiBold'
      colour='BaseLight'
      value={eventName}
    />
  );

  const eventDateTime: ReactNode = (
    <IconText
      iconAsset='CalenderNew'
      text={t('event.shortWeekDayLongDateTime', eventDateTimeAndVenueDetails)}
      color='Charcoal10'
      classes={{ icon: styles.eventInfoDetailIcon, text: styles.eventInfoDetailText }}
    />
  );

  const eventVenue: ReactNode = (
    <IconText
      iconAsset='LocationPinFilled'
      text={t('event.venueNameCityStateCode', eventDateTimeAndVenueDetails)}
      color='Charcoal10'
      classes={{ icon: styles.eventInfoDetailIcon, text: styles.eventInfoDetailText }}
    />
  );

  const payWithRewardsOnly: ReactNode = isPayWithRewardsOnly && (
    <IconText
      iconAsset='Trophy'
      text={t('event.payWithRewardsOnly')}
      color='Charcoal10'
      classes={{ icon: styles.eventInfoDetailIcon, text: styles.eventInfoDetailText }}
    />
  );

  const eventDescriptionView: ReactNode = (
    <ExpandableContent
      mobileMaxLines={3}
      desktopMaxLines={undefined}
      content={eventDescription}
      modalTitleKey='event.aboutThisEvent'
      className={styles.eventDescription}
    />
  );

  const listingsHeader: ReactNode = (
    <Text
      type='Subheading'
      size='Small'
      style='Regular'
      colour='BaseLight'
      value={t('guestListEventPage.selectYourTickets')}
      className={styles.listingsHeader}
    />
  );

  const rewardsSwitcher: ReactNode = (
    <RewardsSwitcher
      eventMetadata={eventMetadata}
      theme='dark'
      listings={listingsMetadata?.listings}
      className={styles.rewardsSwitcher}
    />
  );

  const listings: ReactNode = !isSoldOut && listingCardProps.length
    ? listingCardProps.map((currentListingCardProps: ListingCardProps) => (
      <ListingCard
        {...currentListingCardProps}
        key={currentListingCardProps.key}
      />
    ))
    : <SoldOutCard />;

  return (<>
    {(areListingsLoading || isListingDetailLoading) && <Spinner isOverlay={true} />}
    <div className={cx(styles.guestListEventPage, className)}>
      <div className={styles.topContent}>
        {topContent}
      </div>
      <div className={styles.bottomContent}>
        <div className={styles.eventDetails}>
          <div className={styles.eventDetailsHeader}>
            {cardholderExclusiveBadge}
            {eventNameView}
            {eventDateTime}
            {eventVenue}
            {payWithRewardsOnly}
          </div>
          {eventDescriptionView}
        </div>
        <div className={styles.listings}>
          <SignInDialog
            placement='event_page'
            className={styles.signInDialog}
          />
          {listingsHeader}
          {rewardsSwitcher}
          <div className={styles.listingsContainer}>
            {listings}
          </div>
        </div>
      </div>
    </div>
    {shouldOpenPreCheckout && !!listingDetailQueryParams && !!listingDetail && (
      <GuestListPreCheckoutModal
        {...presenterProps}
        listingDetailQueryParams={listingDetailQueryParams}
        listingDetail={listingDetail}
      />
    )}
  </>);
};
